import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import DOMPurify from 'dompurify';

export interface PdfContentPayload {
  title: string;
  subtitle?: string;
  date?: string;
  metaDescription?: string;
  bodyHtml: string;
  imageDataUrl?: string | null;
  imageLink?: string | null;
  titleFontSize?: number;
  titleIsBold?: boolean;
}

const A4_WIDTH = 794; // px at 96dpi (~8.27in)
const A4_HEIGHT = 1123; // px at 96dpi (~11.69in)

function resolveLink(raw?: string | null): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const hasScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed);
  return hasScheme ? trimmed : `https://${trimmed}`;
}

function buildPdfSurface({
  title,
  subtitle,
  date,
  metaDescription,
  bodyHtml,
  imageDataUrl,
  imageLink,
  titleFontSize,
  titleIsBold,
}: PdfContentPayload): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.style.position = 'absolute';
  wrapper.style.left = '-10000px';
  wrapper.style.top = '0';
  wrapper.style.width = `${A4_WIDTH}px`;
  wrapper.style.minHeight = `${A4_HEIGHT}px`;
  wrapper.className = 'pdf-export-surface';
  wrapper.setAttribute('role', 'presentation');

  const titleElement = document.createElement('h1');
  titleElement.className = 'pdf-title';
  titleElement.textContent = title.trim();
  const resolvedFontSize = Number.isFinite(titleFontSize) && titleFontSize ? titleFontSize : 42;
  titleElement.style.fontSize = `${resolvedFontSize}px`;
  titleElement.style.fontWeight = titleIsBold === false ? '400' : '700';
  wrapper.appendChild(titleElement);

  if (subtitle?.trim()) {
    const subtitleElement = document.createElement('p');
    subtitleElement.className = 'pdf-subtitle';
    subtitleElement.textContent = subtitle.trim();
    wrapper.appendChild(subtitleElement);
  }

  if (date?.trim()) {
    const dateElement = document.createElement('p');
    dateElement.className = 'pdf-date';
    dateElement.textContent = date;
    wrapper.appendChild(dateElement);
  }

  const resolvedLink = resolveLink(imageLink);

  if (imageDataUrl) {
    const image = document.createElement('img');
    image.className = 'pdf-image';
    image.src = imageDataUrl;
    image.alt = metaDescription?.slice(0, 140) ?? title;
    if (resolvedLink) {
      const anchor = document.createElement('a');
      anchor.href = resolvedLink;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.style.display = 'inline-block';
      anchor.style.textDecoration = 'none';
      anchor.style.color = 'inherit';
      anchor.className = 'pdf-image-link';
      anchor.appendChild(image);
      wrapper.appendChild(anchor);
    } else {
      wrapper.appendChild(image);
    }
  }

  const body = document.createElement('div');
  body.className = 'pdf-body';
  body.innerHTML = DOMPurify.sanitize(bodyHtml || '<p></p>');
  wrapper.appendChild(body);

  return wrapper;
}

async function waitForImagesToLoad(container: HTMLElement): Promise<void> {
  const images = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
  if (images.length === 0) return;

  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve, reject) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => reject(new Error('Failed to load image for PDF.')), {
            once: true,
          });
        }),
    ),
  );
}

export async function createPdfBlob(payload: PdfContentPayload): Promise<Blob> {
  const surface = buildPdfSurface(payload);
  document.body.appendChild(surface);

  try {
    await waitForImagesToLoad(surface);

    const imageElement = surface.querySelector<HTMLImageElement>('.pdf-image');
    const wrapperRect = surface.getBoundingClientRect();
    const linkTarget = resolveLink(payload.imageLink);
    const linkArea =
      linkTarget && imageElement
        ? {
            left: imageElement.getBoundingClientRect().left - wrapperRect.left,
            top: imageElement.getBoundingClientRect().top - wrapperRect.top,
            width: imageElement.getBoundingClientRect().width,
            height: imageElement.getBoundingClientRect().height,
          }
        : null;

    const canvas = await html2canvas(surface, {
      scale: 2,
      logging: false,
      useCORS: true,
      backgroundColor: '#ffffff',
    });

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const imgData = canvas.toDataURL('image/png');
    const orientation = canvasWidth >= canvasHeight ? 'landscape' : 'portrait';
    const pdf = new jsPDF({
      orientation,
      unit: 'px',
      format: [canvasWidth, canvasHeight],
      putOnlyUsedFonts: true,
      compress: true,
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvasWidth, canvasHeight, undefined, 'FAST');

    if (linkArea && linkTarget) {
      const scaleX = canvasWidth / wrapperRect.width;
      const scaleY = canvasHeight / wrapperRect.height;
      const x = linkArea.left * scaleX;
      const y = linkArea.top * scaleY;
      const width = linkArea.width * scaleX;
      const height = linkArea.height * scaleY;
      pdf.link(x, y, width, height, { url: linkTarget });
    }
    return pdf.output('blob');
  } finally {
    document.body.removeChild(surface);
  }
}
